import { useEffect, useState } from 'react';
import { apiClient } from '../store/auth.store';
import toast from 'react-hot-toast';

export default function Events() {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    fetchEvents();
  }, [statusFilter]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params: any = { limit: 50, offset: 0 };
      if (statusFilter) params.status = statusFilter;

      const { data } = await apiClient.get('/events', { params });
      setEvents(data.events);
      setPagination(data.pagination);
    } catch (error: any) {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiClient.delete(`/events/${id}`);
      setEvents(events.filter((e) => e.id !== id));
      toast.success('Event deleted');
    } catch (error: any) {
      toast.error('Failed to delete event');
    }
  };

  return (
    <div className="events-page">
      <div className="events-header">
        <h1>Event History</h1>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
        >
          <option value="">All Events</option>
          <option value="pending">Pending</option>
          <option value="processed">Processed</option>
          <option value="ignored">Ignored</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h2>No events yet</h2>
          <p>Create a test donation to see your widget in action</p>
        </div>
      ) : (
        <div className="card">
          <div className="events-table">
            <table>
              <thead>
                <tr>
                  <th>Avatar</th>
                  <th>Username</th>
                  <th>Amount</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id}>
                    <td>
                      {event.avatar ? (
                        <img src={event.avatar} alt="" className="event-avatar" />
                      ) : (
                        <div className="event-avatar-placeholder">
                          {event.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td>{event.username}</td>
                    <td style={{ color: 'var(--success)', fontWeight: 600 }}>
                      ${event.amount.toFixed(2)} {event.currency}
                    </td>
                    <td className="truncate">{event.message}</td>
                    <td>
                      <span className={`status-badge status-${event.status}`}>
                        {event.status}
                      </span>
                    </td>
                    <td>{new Date(event.createdAt).toLocaleString()}</td>
                    <td>
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="btn-delete"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination-info">
            Showing {events.length} of {pagination.total} events
          </div>
        </div>
      )}

      <style>{`
        .events-page h1 {
          font-size: 28px;
          margin-bottom: 5px;
        }
        .events-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }
        .status-filter {
          padding: 10px 14px;
          background: var(--bg-input);
          border: 1px solid var(--border);
          border-radius: 6px;
          color: var(--text-primary);
          font-size: 14px;
        }
        .loading {
          text-align: center;
          padding: 50px;
          color: var(--text-secondary);
        }
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
        }
        .empty-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }
        .empty-state h2 {
          font-size: 20px;
          margin-bottom: 8px;
        }
        .empty-state p {
          color: var(--text-secondary);
        }
        .card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 24px;
          overflow-x: auto;
        }
        .events-table table {
          width: 100%;
          border-collapse: collapse;
        }
        .events-table th {
          text-align: left;
          padding: 12px;
          font-size: 13px;
          color: var(--text-secondary);
          border-bottom: 1px solid var(--border);
        }
        .events-table td {
          padding: 12px;
          font-size: 14px;
          border-bottom: 1px solid var(--border);
        }
        .event-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
        }
        .event-avatar-placeholder {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--primary);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 16px;
        }
        .truncate {
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .status-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          text-transform: capitalize;
        }
        .status-pending {
          background: rgba(255, 215, 64, 0.15);
          color: var(--warning);
        }
        .status-processed {
          background: rgba(0, 200, 83, 0.15);
          color: var(--success);
        }
        .status-ignored {
          background: rgba(255, 82, 82, 0.15);
          color: var(--error);
        }
        .btn-delete {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          padding: 5px;
          opacity: 0.6;
          transition: opacity 0.2s;
        }
        .btn-delete:hover {
          opacity: 1;
        }
        .pagination-info {
          margin-top: 15px;
          font-size: 13px;
          color: var(--text-secondary);
          text-align: center;
        }
      `}</style>
    </div>
  );
}
